import React from "react";
import { imgUrl, money } from "@/lib/api";
import { CheckCircle2, ArrowUpRight, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ProjectCard({ project, donateFallback }) {
  const goal = Number(project.goal) || 0;
  const raised = Number(project.raised) || 0;
  const funded = goal > 0 && raised >= goal;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const fundedBy = project.funded_by;
  const url = project.gofundme_url || donateFallback || "https://gofund.me/15d8987b7";
  const { user, toggleFavorite } = useAuth();
  const isFav = !!user?.favorites?.includes(project.id);
  const onFav = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = "/login"; return; }
    try { await toggleFavorite(project.id); } catch (err) {}
  };

  return (
    <div className="group bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl overflow-hidden flex flex-col hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 ease-out" data-testid={`project-card-${project.id}`}>
      <div className="relative h-56 overflow-hidden">
        {project.image_url ? (
          <img src={imgUrl(project.image_url)} alt={project.image_alt || project.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
        ) : (
          <div className="w-full h-full bg-[#E7EDE2]" />
        )}
        <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[#1F4D2E] text-xs font-bold uppercase tracking-widest px-3 py-1.5">{project.category}</span>
        {funded ? (
          <span className="absolute top-4 right-4 bg-[#16A34A] text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1"><CheckCircle2 size={13} /> Funded</span>
        ) : (
          <span className="absolute top-4 right-4 bg-[#C1272D] text-white text-xs font-bold px-3 py-1.5">Funding Needed</span>
        )}
      </div>

      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-display text-2xl font-semibold text-[#1F4D2E]">{project.title}</h3>
          <button onClick={onFav} data-testid={`fav-btn-${project.id}`} aria-label="Save project"
            className={`shrink-0 mt-1 transition-colors ${isFav ? "text-[#C1272D]" : "text-[#1F4D2E]/30 hover:text-[#C1272D]"}`}>
            <Heart size={20} fill={isFav ? "#C1272D" : "none"} />
          </button>
        </div>
        <p className="text-[#4F6B54] text-sm leading-relaxed flex-1">{project.description}</p>
        {fundedBy && <p className="text-xs text-[#4F6B54] italic mt-3">Made possible by {fundedBy}</p>}

        {goal > 0 && raised > 0 && (
          <div className="mt-6">
            <div className="flex items-end justify-between mb-2">
              <span className="text-lg font-bold text-[#1F4D2E]" data-testid={`project-raised-${project.id}`}>{money(raised)}</span>
              <span className="text-sm text-[#4F6B54]">{funded ? "Fully funded" : `of ${money(goal)}`}</span>
            </div>
            <div className="h-3 bg-[#E7EDE2] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-1000 ease-out ${funded ? "bg-[#16A34A]" : "bg-[#C1272D]"}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs text-[#4F6B54] mt-1.5 inline-block">{pct}% funded</span>
          </div>
        )}

        {goal > 0 && raised === 0 && (
          <div className="mt-6 flex items-center justify-between border-t border-[#1F4D2E]/10 pt-4">
            <span className="text-sm text-[#4F6B54] uppercase tracking-wider">Goal</span>
            <span className="text-lg font-bold text-[#1F4D2E]" data-testid={`project-goal-${project.id}`}>{money(goal)}</span>
          </div>
        )}

        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-btn-${project.id}`}
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#1F4D2E] text-[#FBF8F0] rounded-full px-6 py-3 font-semibold tracking-wide hover:bg-[#C1272D] transition-colors">
          {funded ? "Give Again" : "Sponsor This"} <ArrowUpRight size={17} />
        </a>
      </div>
    </div>
  );
}
